// Polaris PO Intake — admin flow for matching a NYC DOE purchase order to an
// existing invoice, then producing the click-through kit for the vendor portal.
// Backend endpoints live in server.py under /admin/polaris/*.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  Copy,
  ExternalLink,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Building2,
  MapPin,
  Calendar as CalendarIcon,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const POLARIS_URL = "https://www.finance360.org/vendor/vendorportal";

const fmtMoney = (v) =>
  typeof v === "number" ? `$${v.toFixed(2)}` : v ? `$${Number(v).toFixed(2)}` : "$0.00";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const ScoreBadge = ({ score }) => {
  const pct = Math.round((score || 0) * 100);
  let tone = "bg-brand-charcoal/10 text-brand-charcoal/70 border-brand-charcoal/20";
  if (score >= 0.7) tone = "bg-brand-green/15 text-brand-green border-brand-green/40";
  else if (score >= 0.4) tone = "bg-brand-gold/15 text-brand-gold-dark border-brand-gold/40";
  else if (score > 0) tone = "bg-brand-teal/10 text-brand-teal border-brand-teal/30";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.14em] border ${tone}`}
    >
      {pct}% match
    </span>
  );
};

const CopyButton = ({ value, label, testid }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_err) {
      // Clipboard blocked (permission / non-secure origin) — silently skip.
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={doCopy}
      className="group flex items-center gap-2 rounded-xl bg-white border border-brand-teal/25 hover:border-brand-teal hover:bg-brand-teal/5 px-4 py-3 transition-colors"
    >
      <div className="text-left flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-teal">
          {label}
        </div>
        <div className="font-display font-black text-brand-charcoal tabular-nums truncate">
          {value ?? "—"}
        </div>
      </div>
      {copied ? (
        <CheckCircle2 className="h-4 w-4 text-brand-green flex-none" />
      ) : (
        <Copy className="h-4 w-4 text-brand-charcoal/40 group-hover:text-brand-teal flex-none" />
      )}
    </button>
  );
};

const POCard = ({ item, pdfFileId, onAttached, apiOrigin }) => {
  const { po, candidates } = item;
  const [selectedId, setSelectedId] = useState(candidates?.[0]?.order_id || null);
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(null); // { order_id, invoice_number, invoice_pdf_url }
  const [error, setError] = useState("");

  const selected = candidates.find((c) => c.order_id === selectedId);
  const alreadyAttached = candidates.find(
    (c) => (c.po_number || "") === po.po_number
  );
  useEffect(() => {
    // If one candidate is already attached to this PO, mark it as done up front.
    if (alreadyAttached && !attached) {
      setAttached({
        order_id: alreadyAttached.order_id,
        invoice_number: alreadyAttached.invoice_number,
        invoice_pdf_url: `/api/admin/orders/${alreadyAttached.order_id}/pdf`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyAttached?.order_id]);

  const handleAttach = async () => {
    if (!selectedId) return;
    setAttaching(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/polaris/attach/${selectedId}`, {
        po_number: po.po_number,
        po_date: po.po_date,
        po_pdf_file_id: pdfFileId,
        deliver_to_name: po.deliver_to_name,
        deliver_to_address: po.deliver_to_address,
        deliver_to_city: po.deliver_to_city,
        deliver_to_zip: po.deliver_to_zip,
        lda_code: po.lda_code,
        initiator_email: po.initiator_email,
      });
      setAttached({
        order_id: data.order_id,
        invoice_number: data.invoice_number,
        invoice_pdf_url: data.invoice_pdf_url,
      });
      onAttached?.(data);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not attach — try again."
      );
    } finally {
      setAttaching(false);
    }
  };

  const downloadInvoice = async () => {
    if (!attached) return;
    try {
      const resp = await api.get(`/admin/orders/${attached.order_id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${attached.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      window.alert("Download failed — " + (e?.message || "unknown error"));
    }
  };

  return (
    <div
      data-testid={`polaris-po-card-${po.po_number}`}
      className="rounded-2xl bg-white border border-black/5 p-5 sm:p-6 shadow-[0_4px_20px_-12px_rgba(0,0,0,0.15)]"
    >
      {/* PO header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10.5px] font-black uppercase tracking-[0.18em] text-brand-teal">
            NYC DOE Purchase Order
          </div>
          <h3 className="font-display font-black text-[22px] sm:text-[24px] leading-tight text-brand-charcoal mt-1 tabular-nums">
            {po.po_number}
          </h3>
          <div className="mt-1 text-[13px] text-brand-charcoal/65">
            Issued {fmtDate(po.po_date)} · Deliver by {fmtDate(po.principal_signature_date)} ·{" "}
            FY {po.fiscal_year || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
            PO Total
          </div>
          <div className="font-display font-black text-[24px] text-brand-charcoal tabular-nums">
            {fmtMoney(po.total)}
          </div>
        </div>
      </div>

      {/* Deliver-to block */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 rounded-xl bg-brand-cream/40 px-4 py-3 border border-brand-cream">
        <div className="flex gap-2">
          <Building2 className="h-4 w-4 text-brand-teal flex-none mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10.5px] font-black uppercase tracking-[0.14em] text-brand-teal">
              Deliver To
            </div>
            <div className="text-[13.5px] font-bold text-brand-charcoal truncate">
              {po.deliver_to_name || "—"}
            </div>
            {po.lda_code && (
              <div className="text-[11px] text-brand-charcoal/60 tabular-nums">
                LDA {po.lda_code}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <MapPin className="h-4 w-4 text-brand-teal flex-none mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10.5px] font-black uppercase tracking-[0.14em] text-brand-teal">
              Address
            </div>
            <div className="text-[13.5px] text-brand-charcoal truncate">
              {po.deliver_to_address || "—"}
            </div>
            <div className="text-[11px] text-brand-charcoal/60">
              {po.deliver_to_city || ""} {po.deliver_to_zip || ""}
            </div>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="mt-4">
        <div className="text-[10.5px] font-black uppercase tracking-[0.14em] text-brand-charcoal/55">
          Line Items ({po.line_items?.length || 0})
        </div>
        <ul className="mt-2 rounded-xl border border-brand-charcoal/10 divide-y divide-brand-charcoal/8">
          {(po.line_items || []).map((li, i) => (
            <li key={`${po.id || "po"}-line-${i}-${li.description || ""}`} className="flex items-center justify-between px-3 py-2 text-[13px]">
              <span className="text-brand-charcoal truncate">
                <span className="tabular-nums font-bold text-brand-charcoal/60 mr-2">
                  ×{li.quantity}
                </span>
                {li.description}
              </span>
              <span className="tabular-nums font-bold text-brand-charcoal">
                {fmtMoney(li.line_total)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Attached state → Polaris cheat sheet */}
      {attached ? (
        <div
          data-testid={`polaris-cheatsheet-${po.po_number}`}
          className="mt-5 rounded-2xl border-2 border-brand-green/40 bg-brand-green/6 p-4 sm:p-5"
        >
          <div className="flex items-center gap-2 text-brand-green font-black uppercase tracking-[0.14em] text-[11px]">
            <CheckCircle2 className="h-4 w-4" />
            Attached · Ready for Polaris upload
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <CopyButton
              value={po.po_number}
              label="PO # (paste to search)"
              testid={`polaris-copy-po-${po.po_number}`}
            />
            <CopyButton
              value={attached.invoice_number}
              label="Our Invoice # (enter in Polaris)"
              testid={`polaris-copy-invoice-${po.po_number}`}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={downloadInvoice}
              data-testid={`polaris-download-invoice-${po.po_number}`}
              className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[12px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
            >
              <FileText className="h-4 w-4" />
              Download Invoice PDF
            </button>
            <a
              href={POLARIS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`polaris-open-portal-${po.po_number}`}
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-white px-4 py-2 text-[12px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal/85 transition-colors"
            >
              Open Polaris
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          <ol className="mt-4 space-y-1.5 text-[12.5px] text-brand-charcoal/80 leading-snug">
            <li>1. In Polaris → click <b>Create Invoice</b>.</li>
            <li>2. Paste the PO # <b>{po.po_number}</b> into the search field.</li>
            <li>3. Enter our invoice # <b>{attached.invoice_number}</b> in the Invoice Number field.</li>
            <li>4. Upload the downloaded PDF <b>three times</b> — once for Invoice, once for Service Agreement / Scope of Work, once for Work Order.</li>
            <li>5. Click <b>Save / Submit</b> in Polaris. Done.</li>
          </ol>
        </div>
      ) : (
        <>
          <div className="mt-5">
            <div className="text-[10.5px] font-black uppercase tracking-[0.14em] text-brand-charcoal/55">
              Match with existing invoice
            </div>
            <div className="mt-2 space-y-2">
              {candidates.length === 0 && (
                <div className="rounded-xl border border-brand-charcoal/10 bg-brand-cream/30 px-4 py-3 text-[13px] text-brand-charcoal/70">
                  No candidate invoices found. Create the invoice first, then re-upload the PO.
                </div>
              )}
              {candidates.map((c) => (
                <label
                  key={c.order_id}
                  data-testid={`polaris-candidate-${po.po_number}-${c.order_id}`}
                  className={`block rounded-xl border-2 px-4 py-3 cursor-pointer transition-all ${
                    selectedId === c.order_id
                      ? "border-brand-teal bg-brand-teal/6"
                      : "border-brand-charcoal/10 hover:border-brand-teal/40"
                  }`}
                >
                  <input
                    type="radio"
                    name={`cand-${po.po_number}`}
                    value={c.order_id}
                    checked={selectedId === c.order_id}
                    onChange={() => setSelectedId(c.order_id)}
                    className="sr-only"
                  />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-black text-brand-charcoal tabular-nums">
                          {c.invoice_number || "no #"}
                        </span>
                        <ScoreBadge score={c.score} />
                        {c.po_number && (
                          <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-brand-gold-dark bg-brand-gold/15 px-2 py-0.5 rounded-full">
                            PO {c.po_number}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[13px] text-brand-charcoal/80 truncate">
                        {c.customer_name || c.organization || "—"}
                        {c.organization && c.customer_name !== c.organization && (
                          <span className="text-brand-charcoal/50"> · {c.organization}</span>
                        )}
                      </div>
                      <div className="mt-1 text-[11.5px] text-brand-charcoal/55">
                        Event {fmtDate(c.event_date)}
                        {c.event_time && ` @ ${c.event_time}`}
                        {c.zip_code && ` · ZIP ${c.zip_code}`}
                        {c.status && ` · ${c.status}`}
                      </div>
                      {Object.keys(c.reasons || {}).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {Object.entries(c.reasons).map(([k, v]) => (
                            <span
                              key={k}
                              className="text-[10.5px] px-2 py-0.5 rounded-full bg-brand-charcoal/6 text-brand-charcoal/70"
                            >
                              <b>{k}:</b> {v}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="tabular-nums font-black text-brand-charcoal text-[16px]">
                      {fmtMoney(c.total)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700 flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 flex-none mt-0.5" />
              {error}
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={handleAttach}
              disabled={!selectedId || attaching}
              data-testid={`polaris-attach-${po.po_number}`}
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-white px-5 py-3 text-[12px] font-black uppercase tracking-[0.12em] hover:bg-brand-teal/85 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {attaching ? "Attaching…" : `Attach ${po.po_number} to ${selected?.invoice_number || "invoice"}`}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const PolarisPOTab = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // {items, pdf_file_id, po_count}
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type === "application/pdf") setFile(f);
    else if (f) setError("Please drop a PDF file.");
  }, []);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/polaris/parse-po", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not parse the PDF."
      );
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const totalDollars = useMemo(() => {
    if (!result?.items) return 0;
    return result.items.reduce((sum, it) => sum + (it.po?.total || 0), 0);
  }, [result]);

  return (
    <div className="space-y-6" data-testid="polaris-tab">
      <div>
        <h2 className="font-display font-black text-[26px] sm:text-[30px] text-brand-charcoal tracking-tight">
          Polaris PO Intake
        </h2>
        <p className="mt-1 text-[13.5px] text-brand-charcoal/70 max-w-2xl leading-snug">
          Drop the NYC DOE purchase-order PDF from your Outlook inbox. We&apos;ll
          parse every PO in the file, match it to an existing invoice in your
          system, stamp the PO # onto it, and hand you a ready-to-paste kit
          for the Polaris vendor portal.
        </p>
      </div>

      {!result && (
        <div
          ref={dropRef}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          data-testid="polaris-drop-zone"
          className={`rounded-2xl border-2 border-dashed p-8 sm:p-12 text-center transition-all ${
            isDragging
              ? "border-brand-teal bg-brand-teal/8"
              : "border-brand-charcoal/25 bg-white"
          }`}
        >
          <UploadCloud
            className={`h-10 w-10 mx-auto ${
              isDragging ? "text-brand-teal" : "text-brand-charcoal/40"
            }`}
          />
          <div className="mt-3 font-display font-black text-[18px] text-brand-charcoal">
            {file ? file.name : "Drop the DOE PO PDF here"}
          </div>
          <div className="text-[12.5px] text-brand-charcoal/60 mt-1">
            {file
              ? `${(file.size / 1024).toFixed(1)} KB · ready to parse`
              : "…or click below to pick a file. Multiple POs per PDF supported."}
          </div>
          <div className="mt-4 flex justify-center gap-2 flex-wrap">
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              data-testid="polaris-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setFile(f);
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="polaris-pick-file-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/20 px-4 py-2 text-[12px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors"
            >
              Pick PDF
            </button>
            {file && (
              <>
                <button
                  type="button"
                  onClick={upload}
                  disabled={uploading}
                  data-testid="polaris-parse-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-white px-5 py-2 text-[12px] font-black uppercase tracking-[0.12em] hover:bg-brand-teal/85 disabled:opacity-50 transition-colors"
                >
                  {uploading ? "Parsing…" : "Parse purchase orders"}
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="polaris-clear-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/70 hover:text-brand-charcoal hover:border-brand-charcoal/40 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div
          data-testid="polaris-error"
          className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700 flex gap-2 items-start"
        >
          <AlertTriangle className="h-4 w-4 flex-none mt-0.5" />
          {error}
        </div>
      )}

      {result && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10.5px] font-black uppercase tracking-[0.16em] text-brand-teal">
                Parsed
              </div>
              <div className="font-display font-black text-[20px] text-brand-charcoal">
                {result.po_count} purchase order{result.po_count === 1 ? "" : "s"} ·{" "}
                <span className="tabular-nums">{fmtMoney(totalDollars)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={reset}
                data-testid="polaris-reset-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Upload another PDF
              </button>
              <a
                href={POLARIS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="polaris-portal-link"
                className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-white px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em] hover:bg-brand-teal/85 transition-colors"
              >
                Open Polaris
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          <div className="grid gap-4">
            {result.items.map((it) => (
              <POCard
                key={it.po.po_number}
                item={it}
                pdfFileId={result.pdf_file_id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PolarisPOTab;
